import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from './api';

/**
 * Aviso no celular (Mobile2): inscreve ESTE aparelho no Web Push. A permissão é
 * do navegador; a inscrição fica na API (POST /push/subscriptions).
 */

export type PushState =
  | 'loading'
  | 'unsupported' // navegador sem push
  | 'needs-install' // iPhone/iPad fora da tela inicial (a Apple exige instalar)
  | 'server-off' // servidor sem chaves VAPID
  | 'denied' // a pessoa bloqueou as notificações no navegador
  | 'off'
  | 'on';

const isIos = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

const supported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

function keyBytes(base64: string): Uint8Array {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = atob(padded);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

const status = async () =>
  (await api.get<{ publicKey: string | null; devices: number }>('/push/status')).data;

async function registration() {
  return (
    (await navigator.serviceWorker.getRegistration()) ??
    navigator.serviceWorker.register('/sw.js')
  );
}

async function currentState(): Promise<PushState> {
  if (isIos() && !isStandalone()) return 'needs-install';
  if (!supported()) return 'unsupported';
  const { publicKey } = await status();
  if (!publicKey) return 'server-off';
  if (Notification.permission === 'denied') return 'denied';
  const subscription = await (await registration()).pushManager.getSubscription();
  return subscription && Notification.permission === 'granted' ? 'on' : 'off';
}

export function usePush() {
  // Situação lida do aparelho uma vez; ligar/desligar sobrepõe na hora.
  const initial = useQuery({
    queryKey: ['push-state'],
    queryFn: () => currentState().catch((): PushState => 'unsupported'),
    staleTime: Infinity,
  });
  const [override, setState] = useState<PushState | null>(null);
  const state: PushState = override ?? initial.data ?? 'loading';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'off');
        return;
      }
      const { publicKey } = await status();
      if (!publicKey) {
        setState('server-off');
        return;
      }
      const reg = await registration();
      await navigator.serviceWorker.ready;
      const subscription =
        (await reg.pushManager.getSubscription()) ??
        (await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: keyBytes(publicKey) as BufferSource,
        }));
      const { endpoint, keys } = subscription.toJSON();
      await api.post('/push/subscriptions', { endpoint, keys });
      setState('on');
    } catch (err) {
      setError((err as Error).message || 'Não foi possível ativar os avisos');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      const subscription = await (await registration()).pushManager.getSubscription();
      if (subscription) {
        await api.delete('/push/subscriptions', { data: { endpoint: subscription.endpoint } });
        await subscription.unsubscribe();
      }
      setState('off');
    } catch (err) {
      setError((err as Error).message || 'Não foi possível desativar');
    } finally {
      setBusy(false);
    }
  };

  return { state, busy, error, enable, disable };
}

/** Texto curto de cada situação, para a tela. */
export const PUSH_HINTS: Record<PushState, string> = {
  loading: 'Verificando…',
  unsupported:
    'Este navegador não recebe notificações. No celular, use o Chrome (Android) ou o app instalado (iPhone).',
  'needs-install':
    'No iPhone, instale o app primeiro: Compartilhar › "Adicionar à Tela de Início". Depois abra pelo ícone e ative aqui.',
  'server-off': 'Os avisos no celular ainda não estão ligados no servidor.',
  denied:
    'As notificações estão bloqueadas para este site. Libere nas configurações do navegador (cadeado ao lado do endereço) e tente de novo.',
  off: 'Receba um aviso no celular quando chegar lead novo para você, mesmo com o app fechado.',
  on: 'Avisos ligados neste aparelho.',
};
